import { BrowserRouter, Routes, Route } from "react-router-dom";
import { useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider } from "./context/AuthContext";
import { NotificationProvider } from "./context/NotificationContext";
import Layout from "./components/Layout";
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Dashboard from "./pages/Dashboard";
import Lakes from "./pages/Lakes";
import LakeDetail from "./pages/LakeDetail";
import Shop from "./pages/Shop";
import ProductDetail from "./pages/ProductDetail";
import Events from "./pages/Events";
import Community from "./pages/Community";
import Messages from "./pages/Messages";
import Subscription from "./pages/Subscription";
import Profile from "./pages/Profile";
import Learn from "./pages/Learn";
import LearnErrorBoundary from "./components/LearnErrorBoundary";
import LeaguePage from "./pages/LeaguePage";
import Competitions from "./pages/Competitions";
import AdminCompetitions from "./pages/AdminCompetitions";
import AdminModeration from "./pages/AdminModeration";
import AdminMaintenance from "./pages/AdminMaintenance";
import AdminAudit from "./pages/AdminAudit";
import AdminAnalytics from "./pages/AdminAnalytics";
import AdminLearn from "./pages/AdminLearn";
import Members from "./pages/Members";
import PublicProfile from "./pages/PublicProfile";
import PaymentSuccess from "./pages/PaymentSuccess";
import PaymentCancel from "./pages/PaymentCancel";
import Checkout from "./pages/Checkout";
import OrderConfirmation from "./pages/OrderConfirmation";
import MyOrders from "./pages/MyOrders";
import AdminDashboard from "./pages/AdminDashboard";
import LegalPage from "./pages/LegalPage";
import About from "./pages/About";
import Contact from "./pages/Contact";
import "./App.css";

function App() {
  // Global axios response interceptor for 402 Payment Required —
  // surfaces a friendly upgrade toast & redirects to /subscription when the
  // backend gates a feature behind Pro/Elite.
  useEffect(() => {
    const id = axios.interceptors.response.use(
      (res) => res,
      (error) => {
        if (error?.response?.status === 402) {
          const d = error.response.data?.detail || {};
          const required = d.required_tier ? d.required_tier.toUpperCase() : "Pro";
          toast.error(d.message || `${required} membership required to use this feature.`, {
            action: {
              label: "Upgrade",
              onClick: () => {
                window.location.href = d.upgrade_url || "/subscription";
              },
            },
            duration: 8000,
          });
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(id);
  }, []);

  return (
    <AuthProvider>
      <NotificationProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/payment/cancel" element={<PaymentCancel />} />
            <Route path="/order-confirmation" element={<OrderConfirmation />} />
            {/* Legal & marketing — no auth required */}
            <Route path="/terms" element={<LegalPage />} />
            <Route path="/privacy" element={<LegalPage />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route element={<Layout />}>
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/lakes" element={<Lakes />} />
              <Route path="/lakes/:id" element={<LakeDetail />} />
              <Route path="/shop" element={<Shop />} />
              <Route path="/shop/:id" element={<ProductDetail />} />
              <Route path="/checkout" element={<Checkout />} />
              <Route path="/my-orders" element={<MyOrders />} />
              <Route path="/events" element={<Events />} />
              <Route path="/community" element={<Community />} />
              <Route path="/messages" element={<Messages />} />
              <Route path="/learn" element={<LearnErrorBoundary><Learn /></LearnErrorBoundary>} />
              <Route path="/league" element={<LeaguePage />} />
              <Route path="/competitions" element={<Competitions />} />
              <Route path="/admin/competitions" element={<AdminCompetitions />} />
              <Route path="/admin/moderation" element={<AdminModeration />} />
              <Route path="/admin/maintenance" element={<AdminMaintenance />} />
              <Route path="/admin/audit" element={<AdminAudit />} />
              <Route path="/admin/analytics" element={<AdminAnalytics />} />
              <Route path="/admin/learn" element={<AdminLearn />} />
              <Route path="/members" element={<Members />} />
              <Route path="/u/:userId" element={<PublicProfile />} />
              <Route path="/subscription" element={<Subscription />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/admin" element={<AdminDashboard />} />
            </Route>
          </Routes>
          <Toaster position="top-center" richColors closeButton duration={5000} />
        </BrowserRouter>
      </NotificationProvider>
    </AuthProvider>
  );
}

export default App;
